import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Brain, Layers, Image as ImageIcon, Database, BookOpen } from 'lucide-react';
import { Badge } from '../../../../../components/ui/Badge';
import { IssueRow, MODULE_CONFIG } from '../shared';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
interface QuestionDetailPanelProps {
  row: UnifiedQuestionRow;
}
export function QuestionDetailPanel({
  row
}: QuestionDetailPanelProps) {
  const modConfig = MODULE_CONFIG[row.moduleCode] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  return <div className="px-4 py-4 bg-gray-50 border-t border-gray-100" data-id="element-4376">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4377">
        {/* Left Column: Pipeline Issues */}
        <div className="space-y-4" data-id="element-4378">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-4379">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-4380" /> Pipeline Issues
          </h4>

          {row.pipelineIssues.length === 0 ? <div className="p-4 bg-white rounded-lg border border-gray-200 text-center" data-id="element-4381">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-4382" />
              <p className="text-sm text-gray-600" data-id="element-4383">
                Geen pipeline issues gevonden.
              </p>
            </div> : <div className="space-y-2" data-id="element-4384">
              {row.pipelineIssues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-4385" />)}
            </div>}
        </div>

        {/* Right Column: Metadata & Quality */}
        <div className="space-y-6" data-id="element-4386">
          {/* Quality & Metadata */}
          <div data-id="element-4387">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4388">
              <Brain className="w-3.5 h-3.5" data-id="element-4389" /> Kwaliteit & Metadata
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4" data-id="element-4390">
              <div className="grid grid-cols-2 gap-4" data-id="element-4391">
                <div data-id="element-4392">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4393">Cognitief Niveau</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-4394">
                    Niveau {row.cognitiveLevel}
                  </p>
                </div>
                <div data-id="element-4395">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4396">Module & Tier</p>
                  <div className="flex items-center gap-1.5" data-id="element-4397">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${modConfig.bgClass} text-white`} data-id="element-4398">
                      
                      {row.moduleCode}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-4399">
                      Tier {row.tier}
                    </span>
                  </div>
                </div>
                <div data-id="element-4400">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4401">Vraagtype</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-4402">
                    {row.questionType}
                  </p>
                </div>
                <div data-id="element-4403">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4404">Skill</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-4405">
                    {row.skill}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seed Status */}
          <div data-id="element-4406">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4407">
              <Database className="w-3.5 h-3.5" data-id="element-4408" /> Seed Status
            </h4>

            {row.seedWarnings.length === 0 ? <div className="p-3 bg-white rounded-lg border border-gray-200 flex items-center gap-2" data-id="element-4409">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-4410" />
                <span className="text-sm text-gray-600" data-id="element-4411">
                  In sync met taxonomy
                </span>
              </div> : <div className="space-y-2" data-id="element-4412">
                {row.seedWarnings.map((warn, i) => <div key={i} className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 font-mono" data-id="element-4413">
                
                    {warn}
                  </div>)}
              </div>}
          </div>

          {/* Visual Status */}
          <div data-id="element-4414">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4415">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-4416" /> Visuele Hulpmiddelen
            </h4>

            <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-4417">
              {row.hasVisualProblems ? <div className="flex items-start gap-2 text-red-700" data-id="element-4418">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-4419" />
                  <span className="text-sm" data-id="element-4420">
                    Er zijn problemen met de visuals (zie pipeline issues).
                  </span>
                </div> : <div className="flex items-start gap-2 text-emerald-700" data-id="element-4421">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-4422" />
                  <span className="text-sm" data-id="element-4423">
                    Visuals zijn geldig en correct geconfigureerd.
                  </span>
                </div>}
            </div>
          </div>

          {/* Pedagogisch Contract */}
          <div data-id="element-4424">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4425">
              <BookOpen className="w-3.5 h-3.5" data-id="element-4426" /> Pedagogisch Contract
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-4427">
              {(() => {
              const pedagogyIssues = row.pipelineIssues.filter(i => i.domain === 'pedagogy');
              const hasPedagogyBlockers = pedagogyIssues.some(i => i.kind === 'blocker');
              const hasPedagogyCoaches = pedagogyIssues.some(i => i.kind === 'coach');
              return <>
                    <div className="grid grid-cols-2 gap-3" data-id="element-4428">
                      <div data-id="element-4429">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-4430">DOK Niveau</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-4431">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DOK_MISSING') ? <span className="text-amber-600" data-id="element-4432">
                              Niet ingesteld
                            </span> : 'Ingesteld'}
                        </p>
                      </div>
                      <div data-id="element-4433">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-4434">
                          Misconceptie Tags
                        </p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-4435">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_UNTAGGED') ? <span className="text-amber-600" data-id="element-4436">Ontbreekt</span> : pedagogyIssues.some(i => i.code === 'PEDAGOGY_MISCONCEPTION_MISSING') ? <span className="text-amber-500" data-id="element-4437">Gedeeltelijk</span> : <span className="text-emerald-600" data-id="element-4438">Compleet</span>}
                        </p>
                      </div>
                    </div>
                    {pedagogyIssues.length === 0 ? <div className="flex items-center gap-2 pt-1" data-id="element-4439">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-4440" />
                        <span className="text-sm text-gray-600" data-id="element-4441">
                          Pedagogisch contract voldoet aan alle eisen.
                        </span>
                      </div> : <div className="flex items-center gap-2 pt-1" data-id="element-4442">
                        {hasPedagogyBlockers ? <XCircle className="w-4 h-4 text-red-500" data-id="element-4443" /> : <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-4444" />}
                        <span className="text-sm text-gray-600" data-id="element-4445">
                          {pedagogyIssues.length} pedagogische{' '}
                          {pedagogyIssues.length === 1 ? 'bevinding' : 'bevindingen'}
                          {hasPedagogyBlockers ? ' (blocker)' : hasPedagogyCoaches ? ' (verbeterpunt)' : ''}
                        </span>
                      </div>}
                  </>;
            })()}
            </div>
          </div>
        </div>
      </div>
    </div>;
}